"use client"

import { Suspense, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react"

import { usePathname, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import { flushSync } from "react-dom"

import { PdpBuyCta } from "@/components/marketplace/pdp/buy-cta"
import { PdpGallery } from "@/components/marketplace/pdp/gallery"
import { PdpHead } from "@/components/marketplace/pdp/head"
import { PdpOptionPicker } from "@/components/marketplace/pdp/option-picker"
import { PdpInfoRow } from "@/components/marketplace/pdp/perks"
import { PdpPriceHero } from "@/components/marketplace/pdp/price-hero"
import { PdpStickyBuyBar } from "@/components/marketplace/pdp/sticky-buy-bar"
import { Text } from "@/components/ui"
import { isCheckoutSheetOpen } from "@/features/checkout/checkout-sheet"
import { useBrowserProductPricing } from "@/features/product/hooks/use-browser-product-pricing"
import { sendPdpGtmViewEvents } from "@/features/product/pdp-gtm"
import { useCheckoutLoading } from "@/features/product/use-checkout-loading"
import { useEarlyCheckoutTap } from "@/features/product/use-early-checkout-tap"
import { useHeldHeight } from "@/features/product/use-held-height"
import {
  buildCheckoutHref,
  CHECKOUT_LINK_SOURCE,
  dismissCheckoutHold,
  isCheckoutLinkArrival,
  isCheckoutLinkTap,
  readEarlyTapMarker,
  withoutCheckoutLinkSource,
} from "@/lib/checkout/pdp-checkout-link"
import { galleryImages } from "@/lib/helpers/product-images"
import { scrollBehavior } from "@/lib/helpers/scroll-behavior"
import { useLocalizedHref, useLocalizedPush, useShallowQueryParams } from "@/lib/hooks/use-navigation"
import { getPdpPriceView } from "@/lib/money/pdp-pricing"
import { useIsWebView } from "@/lib/providers/WebViewProvider"
import { buildRoute } from "@/lib/routes"
import {
  deriveInitialOptions,
  hasAnyVariantInStock,
  isLastUnit,
  isOptionInStock,
  isVariantInStock,
  matchVariant,
  optionIdToUrlKey,
  type SelectedOptions,
} from "@/lib/services/product-options"
import type { StoreProduct } from "@/platform/api/store-contract"
import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"
import { sendCheckoutStart } from "@/platform/bridge/checkout-start"
import { useNativeBridge } from "@/platform/bridge/use-native-bridge"
import { getStoreProductRating, isPreviewProduct } from "@/platform/catalog/products.client"
import { useFlowRoutes } from "@/platform/experiments/FlowRoutesProvider"
import { trackPdpCheckoutTap } from "@/platform/observability/analytics/pdp-checkout-tracking"
import { trackPdpEarlyTap } from "@/platform/observability/analytics/v2/checkout"
import { logger } from "@/platform/observability/logger"
import { clearLastCheckoutProduct, getLastCheckoutProductRef, saveLastCheckoutProduct } from "@/platform/shopper/carts.client"

const DOCK_CLEARANCE = 78

declare global {
  interface Window {
    receiveMessageFromReactNative?: (message: string) => void
  }
}

const WEB_CHECKOUT_LOADING_MS = 5000

export const inlineCtaNeedsStickyBar = (intersectionRatio: number) => intersectionRatio < 1

export const stickyBarVisible = (isPastInlineCta: boolean, hasMeasuredInlineCta: boolean) => hasMeasuredInlineCta && isPastInlineCta

export const dockClearance = (rootHeight: number, dockSlotHost: Pick<DOMRect, "bottom" | "height"> | undefined) =>
  dockSlotHost && dockSlotHost.height > 0 ? Math.max(0, Math.round(rootHeight - dockSlotHost.bottom)) : DOCK_CLEARANCE

export const inlineCtaPastDock = (inlineCta: Pick<DOMRect, "top" | "bottom">, rootHeight: number, clearance: number) =>
  inlineCta.top < 0 || inlineCta.bottom > rootHeight - clearance

export const crossesAtDock = (entry: Pick<IntersectionObserverEntry, "boundingClientRect" | "rootBounds">) =>
  !entry.rootBounds || entry.boundingClientRect.top >= entry.rootBounds.top

const dockSlotHostRect = () =>
  Array.from(document.querySelectorAll<HTMLElement>("[data-bottom-dock] [data-dock-slot-host]"))
    .map((host) => host.getBoundingClientRect())
    .findLast((rect) => rect.height > 0)

interface PdpBuyBoxProps {
  regionId?: string
  product: StoreProduct
  heading?: string
  renderedFor?: string
  breadcrumbs?: ReactNode
  children?: ReactNode
}

export const PdpBuyBox = ({ product: publicProduct, regionId, heading, renderedFor, breadcrumbs, children }: PdpBuyBoxProps) => {
  const { product, settled: pricingSettled } = useBrowserProductPricing(publicProduct, regionId, undefined, renderedFor)
  const priceBlock = useHeldHeight<HTMLDivElement>(pricingSettled)
  const t = useTranslations("pdp")
  const tBnpl = useTranslations("marketplace.bnpl")
  const promptId = useId()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const isWebView = useIsWebView()
  const visible = useScreenVisible()
  const updateQuery = useShallowQueryParams()
  const flowRoutes = useFlowRoutes()
  const push = useLocalizedPush()
  const localizedHref = useLocalizedHref()
  const bridge = useNativeBridge()

  const isPreview = isPreviewProduct(product)
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(() => deriveInitialOptions(product, (key) => searchParams.get(key)))
  const selectedVariant = useMemo(() => matchVariant(product.variants, selectedOptions), [product.variants, selectedOptions])

  const selectOption = (optionId: string, value: string) => {
    const nextOptions = { ...selectedOptions, [optionId]: value }
    setSelectedOptions(nextOptions)
    setIsPromptingOption(false)
    syncOptionsToUrl(product, nextOptions)
  }

  const [isCheckoutLoading, setIsCheckoutLoading] = useCheckoutLoading(!flowRoutes && isCheckoutSheetOpen(searchParams, isWebView))
  useEffect(() => {
    window.receiveMessageFromReactNative = (message) => {
      try {
        setIsCheckoutLoading((JSON.parse(message) as { isLoading: boolean }).isLoading)
      } catch (error) {
        logger.error("pdp.native_message.parse_failed", { productId: product.id }, error as Error)
      }
    }
    return () => {
      delete window.receiveMessageFromReactNative
    }
  }, [product.id, setIsCheckoutLoading])

  const trackedView = useRef<string | null>(null)
  useEffect(() => {
    if (!visible || !pricingSettled) return
    const view = JSON.stringify([product.id, selectedVariant?.id])
    if (trackedView.current === view) return
    trackedView.current = view
    sendPdpGtmViewEvents(product, selectedVariant)
  }, [pricingSettled, product, selectedVariant, visible])

  const handleCheckout = (): boolean => {
    if (missingOptionTitle) {
      setIsPromptingOption(true)
      const picker = optionsRef.current?.children.item(missingOption ? options.indexOf(missingOption) : 0)
      picker?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus({ preventScroll: true })
      optionsRef.current?.scrollIntoView({ behavior: scrollBehavior(), block: "center" })
      return false
    }
    if (!product.id || !selectedVariant?.id) {
      logger.error("pdp.checkout.missing_variant", { productId: product.id })
      return false
    }
    trackPdpCheckoutTap(product, selectedVariant, price?.amount ?? null)

    if (sendCheckoutStart(selectedVariant.id, crypto.randomUUID(), bridge, () => setIsCheckoutLoading(false))) {
      setIsCheckoutLoading(true)
      return true
    }

    if (flowRoutes) {
      setIsCheckoutLoading(true)
      window.setTimeout(() => setIsCheckoutLoading(false), WEB_CHECKOUT_LOADING_MS)
      push(buildRoute.checkout({ productId: product.id, variantId: selectedVariant.id, previewHandle: isPreview ? product.handle : undefined }))
      return true
    }

    // Preview products are only reachable by handle, so a saved pointer — which the cart
    // re-fetches by id — could never resolve; drop any stale one instead of adding another.
    if (isPreview) {
      if (getLastCheckoutProductRef()?.id === product.id) clearLastCheckoutProduct()
    } else {
      saveLastCheckoutProduct({ product, variant: selectedVariant })
    }
    setIsCheckoutLoading(true)
    window.setTimeout(() => setIsCheckoutLoading(false), WEB_CHECKOUT_LOADING_MS)
    updateQuery({ signup: "1", productId: product.id, variantId: selectedVariant.id, preview: isPreview ? "1" : null })
    return true
  }

  useEarlyCheckoutTap((tap) => {
    if (product.id) {
      trackPdpEarlyTap({ productId: product.id, variantId: selectedVariant?.id ?? "", held_ms: Date.now() - tap.tappedAt, reloaded: false })
    }
    if (!handleCheckout()) dismissCheckoutHold()
  })

  const replayedLinkTap = useRef(false)
  useEffect(() => {
    if (flowRoutes || replayedLinkTap.current || !isCheckoutLinkArrival(window.location.search)) return
    replayedLinkTap.current = true
    const tapped = isCheckoutLinkTap(window.location.search, document.referrer, window.location.origin, window.location.pathname)
    if (!tapped) {
      window.history.replaceState(null, "", withoutCheckoutLinkSource(window.location.pathname, window.location.search))
      return
    }
    const variantId = new URLSearchParams(window.location.search).get("variantId")
    const variant = product.variants.find((candidate) => candidate.id === variantId)
    if (product.id && variant) {
      const tappedPrice = getPdpPriceView({ product, variantId: variant.id })
      trackPdpCheckoutTap(product, variant, tappedPrice?.amount ?? null)
      const marker = readEarlyTapMarker(window.location.search)
      if (marker) trackPdpEarlyTap({ productId: product.id, variantId: variant.id, held_ms: Date.now() - marker.at, reloaded: true })
      if (!isPreview) saveLastCheckoutProduct({ product, variant })
    }
    window.history.replaceState(null, "", withoutCheckoutLinkSource(window.location.pathname, window.location.search))
  }, [product, isPreview, flowRoutes])

  const optionsRef = useRef<HTMLDivElement>(null)
  const [isPromptingOption, setIsPromptingOption] = useState(false)

  const inlineCtaRef = useRef<HTMLDivElement>(null)
  const [isPastInlineCta, setIsPastInlineCta] = useState(true)
  const [hasMeasuredInlineCta, setHasMeasuredInlineCta] = useState(false)
  const [swapsAtDock, setSwapsAtDock] = useState(false)
  useEffect(() => {
    const inlineCta = inlineCtaRef.current
    if (!inlineCta) return
    let observer: IntersectionObserver | undefined
    let clearance = -1
    let measured = false
    let past = true
    let rootHeight = window.innerHeight
    const observe = () => {
      const next = dockClearance(rootHeight, dockSlotHostRect())
      if (next === clearance) return
      clearance = next
      const rect = inlineCta.getBoundingClientRect()
      if (measured && inlineCtaPastDock(rect, rootHeight, clearance) !== past) {
        past = !past
        flushSync(() => {
          setSwapsAtDock(rect.top >= 0)
          setIsPastInlineCta(past)
        })
      }
      observer?.disconnect()
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry) return
          past = inlineCtaNeedsStickyBar(entry.intersectionRatio)
          setSwapsAtDock(measured && crossesAtDock(entry))
          setIsPastInlineCta(past)
          setHasMeasuredInlineCta(true)
          measured = true
          const observedRootHeight = entry.rootBounds ? Math.round(entry.rootBounds.bottom) + clearance : rootHeight
          if (observedRootHeight !== rootHeight) {
            rootHeight = observedRootHeight
            observe()
          }
        },
        { threshold: 1, rootMargin: `0px 0px -${clearance}px 0px` }
      )
      observer.observe(inlineCta)
    }
    observe()
    const docks = document.querySelectorAll<HTMLElement>("[data-bottom-dock]")
    const resize = new ResizeObserver(() => observe())
    for (const dock of docks) resize.observe(dock)
    let frame = 0
    const followSlide = () => {
      observe()
      const sliding = [...docks].some(
        (dock) => typeof dock.getAnimations === "function" && dock.getAnimations({ subtree: true }).some((animation) => "transitionProperty" in animation)
      )
      frame = sliding ? requestAnimationFrame(followSlide) : 0
    }
    const slide = new MutationObserver(() => {
      if (frame === 0) frame = requestAnimationFrame(followSlide)
    })
    for (const dock of docks) slide.observe(dock, { attributes: true, attributeFilter: ["data-nav-hidden"] })
    window.addEventListener("resize", observe)
    return () => {
      observer?.disconnect()
      resize.disconnect()
      slide.disconnect()
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener("resize", observe)
    }
  }, [])

  const price = getPdpPriceView({ product, variantId: selectedVariant?.id })
  const installment = price?.installment
  const rating = getStoreProductRating(product)
  const images = galleryImages(product)
  const options = (product.options ?? []).filter(hasRealChoices)
  const isSoldOut = !hasAnyVariantInStock(product.variants, isPreview)
  const isFreeShipping = price?.freeShipping ?? false
  const missingOption = isSoldOut ? undefined : options.find((option) => !selectedOptions[option.id])
  const missingOptionTitle = missingOption?.title
  const isCtaDisabled = !isSoldOut && !missingOptionTitle && (!selectedVariant || !isVariantInStock(selectedVariant, isPreview))
  const canLink = !isWebView && !isSoldOut && !missingOptionTitle && !isCtaDisabled && product.id && selectedVariant
  const checkoutHref = !canLink
    ? undefined
    : flowRoutes
      ? localizedHref(
          buildRoute.checkout({ productId: product.id, variantId: selectedVariant.id, previewHandle: isPreview ? product.handle : undefined, source: CHECKOUT_LINK_SOURCE })
        )
      : buildCheckoutHref(pathname, searchParams.toString(), { productId: product.id, variantId: selectedVariant.id, isPreview })

  const lastUnit = isLastUnit(selectedVariant)
  const sellerName = product.store?.name?.trim()

  const optionPickers = options.length > 0 && (
    <div ref={optionsRef} className="mt-375 flex flex-col gap-375 @3xl:mt-500 @3xl:gap-500">
      {options.map((option, index) => (
        <PdpOptionPicker
          key={option.id}
          title={option.title}
          values={[...new Set(option.values.map(({ value }) => value))]}
          selected={selectedOptions[option.id]}
          onSelect={(value) => selectOption(option.id, value)}
          isValueAvailable={(value) => isOptionInStock(product.variants, selectedOptions, option.id, value, isPreview)}
          note={index === options.length - 1 && lastUnit ? t("lastUnitAvailable") : undefined}
          prompt={isPromptingOption && option.id === missingOption?.id ? t("chooseOptionPrompt", { option: option.title.toLowerCase() }) : undefined}
          promptId={option.id === missingOption?.id ? promptId : undefined}
        />
      ))}
    </div>
  )

  return (
    <div className="@container">
      {breadcrumbs && <div className="max-w-takeover mx-auto flex h-11 items-center @3xl:hidden">{breadcrumbs}</div>}
      <div className="@3xl:grid-cols-pdp max-w-takeover mx-auto grid items-start gap-500 @3xl:mx-0 @3xl:max-w-none @3xl:gap-1850">
        <div className="@3xl:top-sticky-page min-w-0 @3xl:sticky">
          <Suspense>
            <PdpGallery images={images} title={product.title ?? ""} productId={product.id} />
          </Suspense>
        </div>
        <div className="flex min-w-0 flex-col">
          {breadcrumbs && <div className="mb-400 hidden @3xl:block">{breadcrumbs}</div>}
          <Suspense>
            <div ref={priceBlock}>
              <PdpHead
                title={heading ?? product.title ?? ""}
                perks={{
                  financingBadge: installment?.interestFree ? tBnpl("noInterest") : installment?.financingMode === "SECURED" ? t("priceHero.moreTime") : null,
                  financingTone: installment?.interestFree || installment?.financingMode !== "SECURED" ? "nointerest" : "moretime",
                  freeShipping: isFreeShipping,
                }}
              />
              {price && <PdpPriceHero className="mt-125 @3xl:mt-250" price={price} />}
            </div>
          </Suspense>
          {optionPickers}
          <PdpInfoRow
            className="mt-500 @3xl:mt-750"
            freeShipping={isFreeShipping}
            rating={rating?.average}
            reviewCount={rating?.count}
            purchaseLabel={installment?.financingMode === "SECURED" ? t("info.securedPurchase") : undefined}
          />
          <div
            ref={inlineCtaRef}
            data-handed-off={hasMeasuredInlineCta && isPastInlineCta ? "" : undefined}
            data-swap={swapsAtDock ? "" : undefined}
            className="cta-handoff mt-500 @3xl:mt-875"
          >
            <PdpBuyCta
              soldOut={isSoldOut}
              buying={isCheckoutLoading}
              disabled={isCtaDisabled}
              needsOption={missingOptionTitle}
              promptId={promptId}
              href={checkoutHref}
              onBuy={handleCheckout}
            />
          </div>
          {sellerName && (
            <Text as="p" data-testid="pdp-sold-by" family="text" tone="inherit" className="text-little @3xl:text-little-lg text-fg-heading/60 mt-300 font-normal @3xl:mt-400">
              {t.rich("soldBy", { name: sellerName, b: (chunks) => <span className="text-fg-heading font-medium">{chunks}</span> })}
            </Text>
          )}
          {children && (
            <Suspense>
              <div className="mt-500 flex flex-col gap-500 @3xl:mt-750 @3xl:gap-750">{children}</div>
            </Suspense>
          )}
        </div>
      </div>
      <PdpStickyBuyBar
        visible={stickyBarVisible(isPastInlineCta, hasMeasuredInlineCta)}
        instant={swapsAtDock}
        soldOut={isSoldOut}
        buying={isCheckoutLoading}
        disabled={isCtaDisabled}
        needsOption={missingOptionTitle}
        promptId={promptId}
        href={checkoutHref}
        onBuy={handleCheckout}
      />
    </div>
  )
}

// An option with a single value (including the "Default Title" pseudo option
// the store API synthesises) offers nothing to pick, so its picker stays hidden.
const hasRealChoices = (option: NonNullable<StoreProduct["options"]>[number]) => new Set(option.values.map(({ value }) => value)).size > 1

const syncOptionsToUrl = (product: StoreProduct, options: SelectedOptions) => {
  const urlKeyByOptionId = optionIdToUrlKey(product)
  const params = new URLSearchParams(window.location.search)
  for (const [optionId, value] of Object.entries(options)) {
    const urlKey = urlKeyByOptionId[optionId]
    if (urlKey) params.set(urlKey, value)
  }
  const query = params.toString()
  window.history.replaceState(null, "", query ? `${window.location.pathname}?${query}` : window.location.pathname)
}
